import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { API, errMsg, fmtTime, TOPIC_NAMES } from "../../lib/api";
import { toast } from "sonner";

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    API.get("/admin/analytics/questions").then(({ data }) => setRows(data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!rows) return <p className="py-16 text-center text-sm text-muted-foreground">Loading analytics…</p>;

  return (
    <div data-testid="admin-analytics-page">
      <h1 className="font-display text-2xl font-bold mb-2" data-testid="admin-analytics-title">Question analytics</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Questions with extreme pass rates, heavy flagging or repeated reports may need editing or retirement.
      </p>
      {rows.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="analytics-empty">No attempt data yet — analytics appear once users start answering questions.</p>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="analytics-table">
            <thead>
              <tr className="border-b border-border text-left text-xs tracking-[0.1em] uppercase text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Question</th>
                <th className="px-4 py-3 font-semibold">Attempts</th>
                <th className="px-4 py-3 font-semibold">% correct</th>
                <th className="px-4 py-3 font-semibold">Top distractor</th>
                <th className="px-4 py-3 font-semibold">Avg time</th>
                <th className="px-4 py-3 font-semibold">Flags</th>
                <th className="px-4 py-3 font-semibold">Reports</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => {
                const problematic = r.attempts >= 5 && (r.correct_pct < 30 || r.correct_pct > 95);
                return (
                  <tr key={r.question_id} className="hover:bg-muted/40 cursor-pointer" data-testid={`analytics-row-${r.qid}`}
                    onClick={() => navigate(`/admin/questions/${r.question_id}`)}>
                    <td className="px-4 py-3 max-w-md">
                      <div className="flex items-center gap-2">
                        {problematic && <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />}
                        <div>
                          <p className="font-medium line-clamp-1">{r.stem}</p>
                          <p className="text-xs text-muted-foreground">{r.qid} · {TOPIC_NAMES[r.topic]} · {r.difficulty} · {r.status}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold">{r.attempts}</td>
                    <td className={`px-4 py-3 font-bold ${r.correct_pct < 30 ? "text-destructive" : r.correct_pct >= 70 ? "text-success" : ""}`}>{r.correct_pct}%</td>
                    <td className="px-4 py-3">{r.top_distractor || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtTime(r.avg_time_ms)}</td>
                    <td className="px-4 py-3">{r.flags}</td>
                    <td className={`px {r.reports > 0 ? "text-destructive font-bold" : ""} px-4 py-3`}>{r.reports}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
